import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Upload, X, Camera, Sparkles, Package, AlertTriangle, Scale } from "lucide-react";
import endlessStorageLogo from "@/assets/endless-storage-logo.png";
import { useToast } from "@/hooks/use-toast";
import { fileToBase64 } from "@/lib/qr-utils";
import { CameraCapture } from "@/components/CameraCapture";
import { AIItemsReview } from "@/components/AIItemsReview";

import { analyzeBoxContents } from "@/lib/ai-vision";
import { Badge } from "@/components/ui/badge";

const ROOMS = [
  "Living Room",
  "Bedroom",
  "Kitchen",
  "Bathroom",
  "Garage",
  "Attic",
  "Basement",
  "Office",
  "Dining Room",
  "Kids Room",
  "Guest Room",
  "Other",
];

const WEIGHT_CLASSES = [
  { value: "light", label: "Light", description: "Under 20 lbs - Easy to carry" },
  { value: "medium", label: "Medium", description: "20-40 lbs - Manageable" },
  { value: "heavy", label: "Heavy", description: "Over 40 lbs - Two person lift" },
];

interface DetectedItem {
  name: string;
  quantity: number;
  room: string;
  notes?: string;
  aiGenerated?: boolean;
}

const BoxLog = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [room, setRoom] = useState("Other");
  const [weightClass, setWeightClass] = useState<string>("");
  const [isFragile, setIsFragile] = useState(false);
  const [photos, setPhotos] = useState<Array<{ url: string; thumbnailUrl: string }>>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStartTime, setAnalysisStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [detectedItems, setDetectedItems] = useState<DetectedItem[]>([]);
  const [showItemsReview, setShowItemsReview] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) {
      navigate('/auth');
      return;
    }
    setUser(currentUser);
  };

  const generateBoxNameSuggestion = (items: DetectedItem[]): string => {
    if (items.length === 0) return "";
    
    const roomCounts = items.reduce((acc, item) => {
      acc[item.room] = (acc[item.room] || 0) + item.quantity;
      return acc;
    }, {} as Record<string, number>);
    
    const dominantRoom = Object.entries(roomCounts)
      .sort(([,a], [,b]) => b - a)[0]?.[0];
    
    if (dominantRoom) {
      return `${dominantRoom} Items`;
    }
    
    return "Mixed Household Items";
  };

  const getMostCommonRoom = (items: DetectedItem[]): string => {
    const roomCounts = items.reduce((acc, item) => {
      acc[item.room] = (acc[item.room] || 0) + item.quantity;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(roomCounts)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || "Other";
  };

  const handleSkipAnalysis = () => {
    setIsAnalyzing(false);
    setAnalysisStartTime(null);
    setElapsedTime(0);
    setShowSkipButton(false);
    
    toast({
      title: "Analysis skipped",
      description: "You can add box details manually below.",
    });
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setIsAnalyzing(true);
    setAnalysisStartTime(Date.now());
    setElapsedTime(0);
    
    const intervalId = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    
    const skipTimeout = setTimeout(() => setShowSkipButton(true), 3000);

    try {
      const file = files[0];
      const imageData = await fileToBase64(file);
      
      setPhotos(prev => [...prev, {
        url: imageData,
        thumbnailUrl: imageData,
      }]);

      try {
        const items = await analyzeBoxContents(imageData);
        
        clearInterval(intervalId);
        clearTimeout(skipTimeout);
        setIsAnalyzing(false);
        setAnalysisStartTime(null);
        setElapsedTime(0);
        setShowSkipButton(false);
        
        if (items.length > 0) {
          const itemsList = items.map(item => ({
            ...item,
            aiGenerated: true,
          }));
          
          setDetectedItems(itemsList);
          
          const formattedDescription = formatItemsDescription(items);
          setDescription(formattedDescription);
          
          const suggestedName = generateBoxNameSuggestion(items);
          setName(suggestedName);
          
          const suggestedRoom = getMostCommonRoom(items);
          setRoom(suggestedRoom);
          
          setShowItemsReview(true);
          
          toast({
            title: "Smart Scan Complete! ✨",
            description: `Found ${items.length} items. Name and room auto-filled!`,
          });
        } else {
          toast({
            title: "No Items Detected",
            description: "No items found. You can add items manually later.",
          });
        }
      } catch (analysisError) {
        clearInterval(intervalId);
        clearTimeout(skipTimeout);
        setIsAnalyzing(false);
        setAnalysisStartTime(null);
        setElapsedTime(0);
        setShowSkipButton(false);
        
        console.error('AI analysis error:', analysisError);
        toast({
          title: "Analysis failed",
          description: "Photo saved. You can add items manually.",
          variant: "destructive",
        });
      }
    } catch (error) {
      clearInterval(intervalId);
      clearTimeout(skipTimeout);
      setIsAnalyzing(false);
      setAnalysisStartTime(null);
      setElapsedTime(0);
      setShowSkipButton(false);
      
      console.error('File reading error:', error);
      toast({
        title: "Upload Error",
        description: "Failed to read the photo. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const formatItemsDescription = (items: Array<{name: string; quantity: number; room: string; notes?: string}>) => {
    const grouped = items.reduce((acc, item) => {
      if (!acc[item.room]) {
        acc[item.room] = [];
      }
      acc[item.room].push(item);
      return acc;
    }, {} as Record<string, Array<{name: string; quantity: number; room: string; notes?: string}>>);

    let description = "📦 Box Contents:\n";
    
    Object.entries(grouped).forEach(([room, roomItems]) => {
      const totalCount = roomItems.reduce((sum, item) => sum + item.quantity, 0);
      description += `- **${room} (${totalCount} total):**\n`;
      
      roomItems.forEach(item => {
        const itemName = item.notes ? `${item.name} — ${item.notes}` : item.name;
        if (item.quantity > 1) {
          description += `  - ${item.quantity}x ${itemName}\n`;
        } else {
          description += `  - ${itemName}\n`;
        }
      });
    });

    return description;
  };

  const removePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const handleCameraCapture = async (imageData: string) => {
    setIsAnalyzing(true);
    setAnalysisStartTime(Date.now());
    setElapsedTime(0);
    
    const intervalId = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    
    const skipTimeout = setTimeout(() => setShowSkipButton(true), 3000);
    
    try {
      const items = await analyzeBoxContents(imageData);
      
      clearInterval(intervalId);
      clearTimeout(skipTimeout);
      setIsAnalyzing(false);
      setAnalysisStartTime(null);
      setElapsedTime(0);
      setShowSkipButton(false);
      
      if (items.length === 0) {
        toast({
          title: "No Items Detected",
          description: "Try retaking the photo with better lighting or add items manually.",
        });
        return;
      }

      setPhotos(prev => [...prev, {
        url: imageData,
        thumbnailUrl: imageData,
      }]);

      const itemsList = items.map(item => ({
        ...item,
        aiGenerated: true,
      }));
      
      setDetectedItems(itemsList);

      const formattedDescription = formatItemsDescription(items);
      setDescription(formattedDescription);
      
      const suggestedName = generateBoxNameSuggestion(items);
      setName(suggestedName);
      
      const suggestedRoom = getMostCommonRoom(items);
      setRoom(suggestedRoom);
      
      setShowItemsReview(true);

      toast({
        title: "Smart Scan Complete! ✨",
        description: `Found ${items.length} items. Name and room auto-filled!`,
      });
    } catch (error) {
      clearInterval(intervalId);
      clearTimeout(skipTimeout);
      setIsAnalyzing(false);
      setAnalysisStartTime(null);
      setElapsedTime(0);
      setShowSkipButton(false);
      
      toast({
        title: "Analysis Failed",
        description: error instanceof Error ? error.message : "Failed to analyze photo.",
        variant: "destructive",
      });
    }
  };

  const handleItemsConfirm = (items: DetectedItem[]) => {
    setDetectedItems(items);
    setShowItemsReview(false);
  };

  const generateBoxNumber = (): string => {
    return `BOX-${crypto.randomUUID().replace(/-/g, '').substring(0, 8).toUpperCase()}`;
  };

  const uploadPhotoToStorage = async (base64Image: string, boxId: string) => {
    if (!user) throw new Error("Not authenticated");

    const base64Data = base64Image.split(',')[1];
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/jpeg' });

    const fileName = `${user.id}/${boxId}/${Date.now()}.jpg`;
    const { error: uploadError } = await supabase.storage
      .from('box-photos')
      .upload(fileName, blob);

    if (uploadError) throw uploadError;

    const { error: photoError } = await supabase
      .from('photos')
      .insert({
        box_id: boxId,
        storage_path: fileName,
      });

    if (photoError) throw photoError;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter a box name.",
        variant: "destructive",
      });
      return;
    }

    if (!weightClass) {
      toast({
        title: "Weight Required",
        description: "Please select a weight class for shipping purposes.",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);

    try {
      if (!user) throw new Error("Not authenticated");

      // Auto-generate unique box number
      const boxNumber = generateBoxNumber();

      // Create box with new fields
      const { data: newBox, error: boxError } = await supabase
        .from('boxes')
        .insert({
          user_id: user.id,
          box_number: boxNumber,
          name: name.trim(),
          description: description.trim() || null,
          room: room,
          location_status: 'home',
          weight_class: weightClass,
          is_fragile: isFragile,
        })
        .select()
        .single();

      if (boxError) throw boxError;

      // Upload photos
      for (const photo of photos) {
        await uploadPhotoToStorage(photo.url, newBox.id);
      }

      // Create items
      if (detectedItems.length > 0) {
        const itemsToInsert = detectedItems.map(item => ({
          box_id: newBox.id,
          name: item.name,
          quantity: item.quantity,
          room: item.room,
          notes: item.notes || null,
          ai_generated: item.aiGenerated || false,
        }));

        const { error: itemsError } = await supabase
          .from('items')
          .insert(itemsToInsert);

        if (itemsError) throw itemsError;
      }

      toast({
        title: "Box Created! 📦",
        description: `${name} has been added to your inventory.`,
      });

      navigate('/dashboard-preview');
    } catch (error) {
      console.error('Error creating box:', error);
      toast({
        title: "Error",
        description: "Failed to create box. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Camera Modal */}
      <CameraCapture
        open={showCamera}
        onCapture={handleCameraCapture}
        onClose={() => setShowCamera(false)}
      />


      {/* Items Review Modal */}
      <AIItemsReview
        open={showItemsReview}
        items={detectedItems}
        onConfirm={handleItemsConfirm}
        onClose={() => setShowItemsReview(false)}
      />
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Back</span>
            </button>
            <img src={endlessStorageLogo} alt="Endless Storage" className="h-10" />
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-2">Log New Box</h1>
          <p className="text-muted-foreground">
            Snap a photo of your box contents and we'll do the rest
          </p>
        </div>

        {/* AI Analysis Progress */}
        {isAnalyzing && (
          <Card className="mb-6 border-primary/20 bg-primary/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Sparkles className="h-5 w-5 text-primary animate-pulse" />
                  <span className="font-medium">Analyzing contents...</span>
                </div>
                <span className="text-sm text-muted-foreground">{elapsedTime}s</span>
              </div>
              <Progress value={Math.min((elapsedTime / 30) * 100, 95)} className="h-2" />
              {showSkipButton && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="mt-4 w-full"
                  onClick={handleSkipAnalysis}
                >
                  Skip Analysis & Add Manually
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Photo Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Camera className="h-5 w-5" />
                Box Photo
              </CardTitle>
              <CardDescription>
                Take a photo of the open box to auto-detect items
              </CardDescription>
            </CardHeader>
            <CardContent>
              {photos.length === 0 ? (
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-32 flex flex-col gap-2"
                    onClick={() => setShowCamera(true)}
                    disabled={isAnalyzing}
                  >
                    <Camera className="h-8 w-8" />
                    <span>Take Photo</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-32 flex flex-col gap-2 relative"
                    disabled={isAnalyzing}
                  >
                    <Upload className="h-8 w-8" />
                    <span>Upload</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={handlePhotoUpload}
                    />
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden">
                      <img
                        src={photo.thumbnailUrl}
                        alt={`Box photo ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute top-2 right-2 p-1 bg-destructive text-destructive-foreground rounded-full"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    className="aspect-square flex flex-col gap-2"
                    onClick={() => setShowCamera(true)}
                    disabled={isAnalyzing}
                  >
                    <Camera className="h-6 w-6" />
                    <span className="text-xs">Add More</span>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>


          {/* Box Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="h-5 w-5" />
                Box Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Box Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Kitchen Items"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="room">Room</Label>
                <Select value={room} onValueChange={setRoom}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a room" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROOMS.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Additional notes about the box contents..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Weight & Handling */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Scale className="h-5 w-5" />
                Weight & Handling
              </CardTitle>
              <CardDescription>
                Help us handle your box safely
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Weight Class *</Label>
                <div className="grid grid-cols-3 gap-3">
                  {WEIGHT_CLASSES.map((wc) => (
                    <button
                      key={wc.value}
                      type="button"
                      onClick={() => setWeightClass(wc.value)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        weightClass === wc.value
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <div className="font-medium">{wc.label}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {wc.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg border-2 border-border">
                <div className="flex items-center gap-3">
                  <AlertTriangle className={`h-5 w-5 ${isFragile ? "text-orange-500" : "text-muted-foreground"}`} />
                  <div>
                    <Label htmlFor="fragile" className="font-medium cursor-pointer">
                      Fragile Contents
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Contains breakable items requiring extra care
                    </p>
                  </div>
                </div>
                <Switch
                  id="fragile"
                  checked={isFragile}
                  onCheckedChange={setIsFragile}
                />
              </div>

              {isFragile && (
                <div className="flex items-start gap-2 p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
                  <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5" />
                  <p className="text-sm text-orange-700 dark:text-orange-300">
                    We'll add extra protection and handling care for this box.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Detected Items Preview */}
          {detectedItems.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Detected Items ({detectedItems.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {detectedItems.slice(0, 8).map((item, index) => (
                    <Badge key={index} variant="secondary">
                      {item.quantity > 1 ? `${item.quantity}x ` : ""}{item.name}
                    </Badge>
                  ))}
                  {detectedItems.length > 8 && (
                    <Badge variant="outline">
                      +{detectedItems.length - 8} more
                    </Badge>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-3"
                  onClick={() => setShowItemsReview(true)}
                >
                  Edit Items
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Submit */}
          <Button 
            type="submit" 
            className="w-full h-12 text-lg"
            disabled={isCreating || isAnalyzing}
          >
            {isCreating ? (
              <>Creating Box...</>
            ) : (
              <>
                <Package className="h-5 w-5 mr-2" />
                Save Box
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default BoxLog;
